import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  useStripe, useElements, PaymentElement, AddressElement,
} from '@stripe/react-stripe-js';

// Inner checkout form. Stripe AddressElement collects name + shipping address with
// autocomplete (automatic mode — free, since a PaymentElement shares this Elements
// group); a "billing same as shipping" toggle reveals a billing AddressElement when
// off. The PaymentElement collects only the payment method — billing_details are
// supplied from the address element(s) at confirm time. On success the order is
// committed server-side AFTER payment (matching the Stripe V2 pilot; the "paid but
// no order" edge remains a known risk). A right rail shows the order summary.
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const summaryShape = PropTypes.shape({
  subtotal: PropTypes.number,
  discount: PropTypes.number,
  shipping: PropTypes.number,
  tax: PropTypes.number,
  total: PropTypes.number,
  coupon: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    detail: PropTypes.string,
    price: PropTypes.number,
  })),
});

// Stripe AddressElement value -> flat fields. name is split into first/last for
// the legacy Order columns.
const flatten = (value) => {
  const a = (value && value.address) || {};
  const name = (value && value.name) || '';
  const [first, ...rest] = name.trim().split(' ');
  return {
    name,
    firstName: first || name,
    lastName: rest.join(' '),
    phone: (value && value.phone) || '',
    line1: a.line1 || '',
    line2: a.line2 || '',
    city: a.city || '',
    state: a.state || '',
    postalCode: a.postal_code || '',
    country: a.country || 'US',
  };
};

const flexRow = { display: 'flex', justifyContent: 'space-between' };
const sum = {
  card: {
    background: '#fff',
    border: '1px solid rgba(0,0,0,.1)',
    borderRadius: 14,
    padding: '1.25rem',
    position: 'sticky',
    top: '1rem',
  },
  row: { ...flexRow, margin: '0.35rem 0', fontSize: '0.95rem' },
  item: {
    ...flexRow, margin: '0.3rem 0', color: '#1f2430', fontSize: '0.9rem',
  },
  saved: { color: '#059669', fontWeight: 600 },
  total: {
    ...flexRow,
    marginTop: '0.6rem',
    paddingTop: '0.6rem',
    borderTop: '1px solid #ececf0',
    fontWeight: 700,
    fontSize: '1.1rem',
    color: '#030213',
  },
  hr: { border: 0, borderTop: '1px solid #ececf0', margin: '0.75rem 0' },
};

const OrderSummary = ({ summary }) => {
  if (!summary) return null;
  return (
    <div style={sum.card}>
      <h2 className="h6" style={{ color: '#030213' }}>Order summary</h2>
      {(summary.items || []).map((it, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} style={sum.item}>
          <span>
            {it.name}
            {it.detail ? <span style={{ color: '#717182' }}>{` · ${it.detail}`}</span> : null}
          </span>
          <span>{money(it.price)}</span>
        </div>
      ))}
      <hr style={sum.hr} />
      <div style={sum.row}>
        <span>Subtotal</span>
        <span>{money(summary.subtotal)}</span>
      </div>
      {summary.discount > 0 && (
        <div style={{ ...sum.row, ...sum.saved }}>
          <span>{`You saved${summary.coupon ? ` (${summary.coupon})` : ''}`}</span>
          <span>{`−${money(summary.discount)}`}</span>
        </div>
      )}
      <div style={sum.row}>
        <span>Shipping</span>
        <span>{summary.shipping > 0 ? money(summary.shipping) : 'FREE'}</span>
      </div>
      <div style={sum.row}>
        <span>Tax</span>
        <span>{money(summary.tax)}</span>
      </div>
      <div style={sum.total}>
        <span>Total</span>
        <span>{money(summary.total)}</span>
      </div>
    </div>
  );
};

OrderSummary.propTypes = { summary: summaryShape };
OrderSummary.defaultProps = { summary: null };

const CheckoutForm = ({
  ordersUrl, completeUrl, email, total, summary, csrfToken,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [contactEmail, setContactEmail] = useState(email || '');
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [shipping, setShipping] = useState(null);
  const [billing, setBilling] = useState(null);
  const [shippingComplete, setShippingComplete] = useState(false);
  const [billingComplete, setBillingComplete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const amount = summary ? summary.total : total;
  const ready = !!stripe && shippingComplete && !!contactEmail && (sameAsShipping || billingComplete);

  const commitOrder = async (paymentIntentId, ship, bill) => {
    const res = await fetch(ordersUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({
        payment_intent_id: paymentIntentId,
        order: {
          first_name: ship.firstName,
          last_name: ship.lastName,
          email: contactEmail,
          phone: ship.phone,
          shipping_first_name: ship.firstName,
          shipping_last_name: ship.lastName,
          shipping_address: ship.line1,
          shipping_address2: ship.line2,
          shipping_city: ship.city,
          shipping_state: ship.state,
          shipping_zip: ship.postalCode,
          address: bill.line1,
          address2: bill.line2,
          city: bill.city,
          state: bill.state,
          zip_code: bill.postalCode,
        },
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Order could not be created.');
    return data;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);

    const ship = flatten(shipping);
    const bill = sameAsShipping ? ship : flatten(billing);

    const { error: submitError } = await elements.submit();
    if (submitError) { setError(submitError.message); setProcessing(false); return; }

    const { error: payError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: window.location.href,
        payment_method_data: {
          billing_details: {
            name: bill.name || ship.name,
            email: contactEmail,
            phone: ship.phone || undefined,
            address: {
              line1: bill.line1,
              line2: bill.line2 || undefined,
              city: bill.city,
              state: bill.state,
              postal_code: bill.postalCode,
              country: bill.country,
            },
          },
        },
      },
    });

    if (payError) { setError(payError.message); setProcessing(false); return; }
    if (!paymentIntent || paymentIntent.status !== 'succeeded') {
      setError('Payment could not be completed. Please try again.');
      setProcessing(false);
      return;
    }

    try {
      const data = await commitOrder(paymentIntent.id, ship, bill);
      window.location.href = `${completeUrl}?order_id=${data.order_id}&email=${encodeURIComponent(data.email)}`;
    } catch (err) {
      setError('Your payment went through, but we could not finish your order. '
        + `Please contact support. (${err.message})`);
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={submit} className="row">
      <div className="col-lg-7">
        <h2 className="h6">Contact</h2>
        <div className="mb-3">
          <label className="form-label" htmlFor="co-email">Email</label>
          <input
            id="co-email"
            type="email"
            className="form-control"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            required
          />
          <div className="form-text">Your receipt and digital downloads are sent here.</div>
        </div>

        <h2 className="h6 mt-3">Shipping</h2>
        <AddressElement
          options={{ mode: 'shipping', fields: { phone: 'always' }, autocomplete: { mode: 'automatic' } }}
          onChange={(ev) => { setShipping(ev.value); setShippingComplete(ev.complete); }}
        />

        <div className="form-check mt-3">
          <input
            id="co-same"
            className="form-check-input"
            type="checkbox"
            checked={sameAsShipping}
            onChange={(e) => setSameAsShipping(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="co-same">Billing address same as shipping</label>
        </div>
        {!sameAsShipping && (
          <div className="mt-2">
            <h2 className="h6">Billing</h2>
            <AddressElement
              options={{ mode: 'billing', autocomplete: { mode: 'automatic' } }}
              onChange={(ev) => { setBilling(ev.value); setBillingComplete(ev.complete); }}
            />
          </div>
        )}

        <h2 className="h6 mt-3">Payment</h2>
        <PaymentElement options={{
          layout: 'tabs',
          fields: {
            billingDetails: {
              name: 'never', email: 'never', phone: 'never', address: 'never',
            },
          },
        }}
        />

        {error && <div className="alert alert-danger mt-3">{error}</div>}

        <button type="submit" className="btn btn-primary w-100 mt-3" disabled={!ready || processing}>
          {processing ? 'Processing…' : `Pay ${money(amount)}`}
        </button>
      </div>

      <div className="col-lg-5 mt-4 mt-lg-0">
        <OrderSummary summary={summary} />
      </div>
    </form>
  );
};

CheckoutForm.propTypes = {
  ordersUrl: PropTypes.string.isRequired,
  completeUrl: PropTypes.string.isRequired,
  email: PropTypes.string,
  total: PropTypes.number,
  summary: summaryShape,
  csrfToken: PropTypes.string.isRequired,
};

CheckoutForm.defaultProps = {
  email: '',
  total: 0,
  summary: null,
};

export default CheckoutForm;
